import { useRequestURL, useState } from '#imports'
import { type Ref, computed, onMounted, ref, watch } from 'vue'

import {
  type GetLegacyServicePickersResponse,
  type GetPickersResponse,
  type PickerService,
  hasItemExtraData,
} from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import type { Rating } from '@backmarket/http-api/src/api-specs-reviews/types/rating'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isBrowser } from '@backmarket/utils/env/isBrowser'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { isEqual } from '@backmarket/utils/object/isEqual'
import { capitalize } from '@backmarket/utils/string/capitalize'

import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'
import { useHistoricalPriceDeal } from '~/scopes/product-core/pages/ProductDetailPage/jp/composables/useHistoricalPriceDeal/useHistoricalPriceDeal'
import { MAX_REVIEWS_DISPLAYED_IN_LIST } from '~/scopes/reviews/reviews-display/constants'
import { useDeliveryBeforeChristmas } from '~/scopes/shipping/composables/useDeliveryBeforeChristmas/useDeliveryBeforeChristmas'

function getMobilePlanOffers(picker?: PickerService): string {
  if (!picker) return 'no_offer'

  const items = picker.items.filter(
    (item) => item.trackingValue !== 'smartphone_only',
  )

  return (
    items
      .filter((item) => hasItemExtraData(item))
      .map((item) => item.trackingValue)
      .join('-') ?? 'no_offer'
  )
}

export function useProductTracking(
  product: Ref<GetProductResponse | undefined>,
  selectedOffer: Ref<GetPickersResponse['selectedOffer']>,
  rating: Ref<Rating | undefined>,
  pickers: Ref<GetPickersResponse | undefined>,
  isPickersResponsePending: Ref<boolean | null>,
  servicesPickers: Ref<GetLegacyServicePickersResponse | undefined>,
  isServicesPickersResponsePending: Ref<boolean | null>,
  isRatingResponsePending: Ref<boolean> = ref(false),
) {
  const { offerType, grade, mobilePlanOfferExperiment } = useUrlParams()
  const { trackProductPage, trackViewItem, addUserContext } = useTracking()
  const { defaultCurrency, countryCode } = useMarketplace().market
  const previousEvent = useState<unknown>(
    'product-page:product-tracking-event',
    () => null,
  )
  const {
    isStandardDeliveryBeforeChristmas,
    isExpressDeliveryBeforeChristmas,
  } = useDeliveryBeforeChristmas(selectedOffer.value?.shipping)
  const experiments = useExperiments()
  const { historicalPrice } = useHistoricalPriceDeal()
  const productEndpointData = computed(() => ({
    id: product.value?.productLegacyId ?? '',
    dealType: 'normal',
    brand: product.value?.brand ?? '',
    category: product.value?.tracking.categoryName ?? '',
    color: product.value?.tracking.color ?? '',
    image: product.value?.images?.[0]?.url ?? '',
    isSwapEnabled: product.value?.isTradeInEligible ?? false,
    model: product.value?.model ?? '',
    name: product.value?.titles.raw ?? '',
    uuid: product.value?.productId ?? '',
    productSimCategory: product.value?.dualSim || '',
    isFlashSales:
      product.value?.promoCodes.some((promoCode) => promoCode.isFlashSale) ??
      false,
    isSellerFlashSales: !!product.value?.hasDeals,
  }))

  const partnerPromoCodes = computed(() => {
    if (
      !product.value ||
      isEmpty(product.value.includedServiceOffers.partnerPromoCodes)
    ) {
      return 'no_offer'
    }

    return product.value.includedServiceOffers.partnerPromoCodes.length > 0
      ? product.value.includedServiceOffers.partnerPromoCodes
          .map((item) => item.partnerName)
          .join('-')
      : 'no_offer'
  })

  const partnerPromoCodesEligibleOffers = computed(
    () =>
      product.value?.includedServiceOffers.partnerPromoCodes
        .map((item) => item.partnerName)
        .join(',') || 'FALSE',
  )

  const offerEndpointData = computed(() => {
    let shippingEvent = 'not_on_time_before_event'

    if (
      isStandardDeliveryBeforeChristmas.value &&
      isExpressDeliveryBeforeChristmas.value
    ) {
      shippingEvent = 'on_time_before_event'
    } else if (isExpressDeliveryBeforeChristmas.value) {
      shippingEvent = 'express_on_time_before_event'
    }

    return {
      currency: selectedOffer.value?.price?.currency ?? '',
      discount: Number(selectedOffer.value?.discount?.price?.amount ?? 0),
      hasExpressShipping: !!selectedOffer.value?.shipping?.express,
      listingId: selectedOffer.value?.offerLegacyId ?? 0,
      merchantId: String(selectedOffer.value?.merchant?.merchantId ?? ''),
      sellerOrigin:
        selectedOffer.value?.merchant?.country.code ||
        selectedOffer.value?.merchant?.country.name ||
        '',
      price: selectedOffer.value?.price?.amount ?? '',
      productHasHistoricalPrice: Boolean(historicalPrice.value),
      shippingDelay: selectedOffer.value?.shipping?.free?.delayInHours ?? 0,
      shippingEvent,
      variant: selectedOffer.value?.grade?.value ?? 0,
    }
  })

  const pickersEndpointData = computed(() => {
    const productHasGoodDealGrade =
      pickers.value?.pickerGroups
        .find((group) => group.id === 'grades')
        ?.items.findIndex((picker) => picker.goodDeal) !== -1

    const gradeSelectedPicker = pickers.value?.pickerGroups
      .find((group) => group.id === 'grades')
      ?.items.find((picker) => picker.selected)

    const hasPremium =
      pickers.value?.pickerGroups.find((group) => group.id === 'grades')?.items
        .length === 4

    const hasPremiumInStock =
      pickers.value?.pickerGroups
        .find((group) => group.id === 'grades')
        ?.items.find((item) => item.parameters?.grade?.value === 9)
        ?.available ?? false

    const batteryPicker = pickers.value?.pickerGroups.find(
      (group) => group.id === 'battery',
    )

    const hasNewBattery = Boolean(batteryPicker)

    const hasNewBatteryInStock = Boolean(batteryPicker?.items[1].available)

    return {
      productPriceTagSelected: gradeSelectedPicker?.goodDeal || false,
      productPriceTagType: productHasGoodDealGrade
        ? 'good_deal_grade'
        : 'no_tag',
      hasPremium,
      hasPremiumInStock,
      hasNewBattery,
      hasNewBatteryInStock,
      isOutOfStock: pickers.value?.isOutOfStock ?? true,
      campaignId: pickers.value?.selectedOffer?.campaignId ?? '',
      populationId: pickers.value?.selectedOffer?.populationId ?? '',
    }
  })

  const servicesPickersEndpointData = computed(() => {
    const mobilePlanPicker = servicesPickers.value?.pickerGroups.find(
      (group) => group.id === 'mobile_plan',
    )

    const mobilePlanOffers = getMobilePlanOffers(mobilePlanPicker)

    const mobilePlanOfferSelected =
      mobilePlanPicker?.items
        .filter((item) => item.trackingValue !== 'smartphone_only')
        .find((picker) => picker.selected)?.trackingValue ?? ''

    return {
      mobilePlanOffers,
      mobilePlanOfferSelected,
    }
  })

  const rateEndpointData = computed(() => ({
    averageRate: rating.value?.averageRate ?? 0,
    hasReviews: (rating.value?.count ?? 0) > 0,
    numberTotalReviews: rating.value?.count ?? 0,
    numberReviewsDisplayed: Math.min(
      rating.value?.count ?? 0,
      experiments['experiment.reviewAmount'] === 'tenReviews'
        ? 10
        : MAX_REVIEWS_DISPLAYED_IN_LIST,
    ),
  }))

  const urlData = computed(() => ({
    mobileDeeplink: `backmarket://product/${
      product.value?.productId
    }?specialOfferType=${offerType?.value ?? '0'}`,
    specialOfferType: offerType?.value ?? '0',
  }))

  // This is not yet set in front-apps, but in pastrami we get some information for the tracking
  // set in the session storage. It's used to know where the user comes from (reco, search, bundle...)
  const sessionStorageData = computed(() => {
    try {
      if (!product.value?.productId || !isBrowser() || !window.sessionStorage) {
        return { list: '' }
      }

      const item = sessionStorage.getItem(product.value?.productId)
      if (!isEmpty(item)) {
        return { list: JSON.parse(item as string).source }
      }
    } catch {
      // fails silently
    }

    return { list: '' }
  })

  // This is used for braze campaigns
  const { hostname, protocol, pathname } = useRequestURL()
  const webUrl = `${protocol}//${hostname}${pathname}`

  const productTracking = computed(() => ({
    ...productEndpointData.value,
    ...offerEndpointData.value,
    ...pickersEndpointData.value,
    ...servicesPickersEndpointData.value,
    ...rateEndpointData.value,
    ...urlData.value,
    ...sessionStorageData.value,
    partnerPromoCodes: partnerPromoCodes.value,
    partnerPromoCodesEligibleOffers: partnerPromoCodesEligibleOffers.value,
    webUrl,
  }))

  // Makes sure we're sending the real data once all calls are finished.
  const isRequestPending = computed(() => {
    return (
      isPickersResponsePending.value ||
      isServicesPickersResponsePending.value ||
      isRatingResponsePending.value
    )
  })

  // Shared GA4 ecommerce payload built from the current offer/product/rating.
  // Reused by view_item (BB-25) and add_to_wishlist (BB-26).
  function buildTrackingPayload() {
    const offer = selectedOffer.value

    return {
      currency: offer?.price?.currency ?? defaultCurrency,
      value: Number(offer?.price?.amount ?? 0),
      item: {
        itemId: String(product.value?.productLegacyId ?? ''),
        itemUuid: product.value?.productId ?? '',
        itemName: product.value?.titles.raw ?? '',
        affiliation: String(offer?.merchant?.merchantId ?? ''),
        discount: Number(offer?.discount?.price?.amount ?? 0),
        itemBrand: capitalize(product.value?.brand ?? ''),
        itemCategory: product.value?.tracking.categoryName ?? '',
        itemVariant: String(offer?.grade?.value ?? ''),
        itemWarrantyDuration: offer?.defaultWarrantyDelay ?? '',
        itemIsTradeInEligible: product.value?.isTradeInEligible ?? false,
        locationId: countryCode,
        price: Number(offer?.price?.amount ?? 0),
      },
    }
  }

  // 🚧 GA4 migration (BB-26): add_to_wishlist payload, fired on favorite click
  // by FavoritesToggle (via ContainerFavoritesToggle). Same item as view_item
  // plus the favorites-specific fields. item_favorited_location is product_page
  // because this composable only powers the PDP.
  const favoriteTrackingInfo = computed(() => {
    const payload = buildTrackingPayload()

    return {
      ...payload,
      item: {
        ...payload.item,
        itemPriceWhenFavorited: payload.item.price,
        itemFavoritedLocation: 'product_page',
      },
    }
  })

  // Skip tracking during SSR
  if (!import.meta.server) {
    watch(
      [grade, offerType, isRequestPending],
      () => {
        if (
          !isRequestPending.value &&
          !isEqual(productTracking.value, previousEvent.value)
        ) {
          trackProductPage(productTracking.value)

          // 🚧 GA4 migration (BB-25): also fire view_item staging event for validation
          trackViewItem(buildTrackingPayload())

          previousEvent.value = productTracking.value
        }
      },
      { immediate: true },
    )
  }

  onMounted(() => {
    if (mobilePlanOfferExperiment?.value) {
      addUserContext({
        mobile_plan_offer_experiment: mobilePlanOfferExperiment.value ?? '',
      })
    }

    const usNewBatteryVariation = experiments['experiment.usNewBattery']
    if (
      usNewBatteryVariation === 'withBatteryPicker' ||
      usNewBatteryVariation === 'withoutBatteryPicker'
    ) {
      addUserContext({ us_new_battery_experiment: usNewBatteryVariation })
    }
  })

  return {
    product: productTracking,
    favoriteTrackingInfo,
  }
}
